/**
 * Utility functions for handling unknown errors in TypeScript catch blocks
 */

/**
 * Type guard to check if an unknown value is an Error object
 */
export const isError = (error: unknown): error is Error => {
  return error instanceof Error;
};

/**
 * Type guard to check if an unknown value has a specific property
 */
export const hasProperty = <T extends string>(
  obj: unknown,
  prop: T
): obj is Record<T, unknown> => {
  return typeof obj === 'object' && obj !== null && prop in obj;
};

/**
 * Safely get error message from unknown error
 */
export const getErrorMessage = (error: unknown): string => {
  if (isError(error)) {
    return error.message;
  }
  if (hasProperty(error, 'message') && typeof error.message === 'string') {
    return error.message;
  }
  return String(error);
};

/**
 * Safely get error name from unknown error (useful for AbortError checks)
 */
export const getErrorName = (error: unknown): string | null => {
  if (isError(error)) {
    return error.name;
  }
  if (hasProperty(error, 'name') && typeof error.name === 'string') {
    return error.name;
  }
  return null;
};

/**
 * Check if error is an AbortError
 */
export const isAbortError = (error: unknown): boolean => {
  return getErrorName(error) === 'AbortError';
};

/**
 * Check if error has a code property (useful for API errors)
 */
export const hasErrorCode = (error: unknown): boolean => {
  return hasProperty(error, 'code');
};

/**
 * Safely get error code from unknown error
 */
export const getErrorCode = (error: unknown): string | number | null => {
  if (hasProperty(error, 'code')) {
    return error.code as string | number;
  }
  return null;
};
